import {
  Children,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
} from "react";
import {
  GetProps,
  Separator as TamaguiSeparator,
  XStack,
  YStack,
  styled,
} from "tamagui";

import { SIZE_CONSTANTS } from "@/theme";
import { DESKTOP_COLS, GUTTER, MOBILE_COLS } from "@/theme/constants";
import { Text } from "./Typography";

/**
 * Base layout components for consistent page structure.
 * All containers use position: "relative" to anchor absolutely-positioned children, matching tamagui v1 behavior
 * tamagui.config.ts sets defaultPosition: "relative" but only manages to apply the element explicitly named "View"
 * We rename many of the components here, causing them to be ineligible for the defaultPosition setting
 */

export const Header = styled(YStack, {
  name: "Header",
  gap: "$100",
  position: "relative",
});

export const Nav = styled(YStack, {
  name: "Nav",
  role: "navigation",
  position: "relative",
});

/**
 * Main content of the page. Contains a flexGrow and is a sibling element to Footer,
 * which will push the footer to the bottom of the screen.
 */
export const Main = styled(YStack, {
  name: "Main",
  role: "main",
  gap: "$200",
  flexGrow: 1,
  position: "relative",
});

/** Used for discrete sections of content within each screen. */
export const Section = styled(YStack, {
  name: "Section",
  gap: "$150",
  position: "relative",
});

/** Used to callout related content on a screen */
export const Aside = styled(YStack, {
  backgroundColor: "$surfaceContainer",
  borderRadius: "$100",
  padding: "$200",
  gap: "$200",
  position: "relative",
});

/** Contains the CTA buttons. Used as a sibling element to Main */
export const Footer = styled(YStack, {
  name: "Footer",
  gap: "$100",
  position: "relative",
  variants: {
    mode: {
      dialog: {
        $lg: {
          flexDirection: "row-reverse",
          alignItems: "center",
          gap: "$300",
        },
      },
    },
  } as const,
});

export const Ul = styled(YStack, {
  name: "Ul",
  role: "list",
  gap: "$100",
});

type LiProps = GetProps<typeof XStack> & { marker?: ReactNode };

export const Li = ({ marker, children, ...props }: LiProps) => {
  const hasMarker = marker !== undefined && marker !== null && marker !== false;
  const renderedMarker =
    typeof marker === "string" || typeof marker === "number" ? (
      <Text bold flexShrink={0}>
        {marker}
      </Text>
    ) : (
      marker
    );

  if (hasMarker) {
    return (
      <XStack role="listitem" alignItems="flex-start" gap="$100" {...props}>
        {renderedMarker}
        <YStack flexShrink={1} gap="$50">
          {children}
        </YStack>
      </XStack>
    );
  }
  return (
    <XStack role="listitem" gap="$100" {...props}>
      {children}
    </XStack>
  );
};

export const Ol = ({ children, ...props }: GetProps<typeof YStack>) => (
  <YStack role="list" gap="$100" {...props}>
    {Children.map(children, (child, i) =>
      isValidElement(child)
        ? cloneElement(child as ReactElement<LiProps>, { marker: `${i + 1}.` })
        : child,
    )}
  </YStack>
);

export const getGridLayout = (colSpan: number) => {
  const calcWidth = (cols: number, span: number) =>
    span >= cols
      ? "100%"
      : `calc(((100% - ${cols - 1} * ${GUTTER}px) / ${cols}) * ${span} + ${GUTTER * (span - 1)}px)`;

  return {
    width: calcWidth(MOBILE_COLS, colSpan),
    $lg: { width: calcWidth(DESKTOP_COLS, colSpan) },
  };
};

export const Grid = styled(YStack, {
  name: "Grid",
  flexDirection: "row",
  flexWrap: "wrap",
  width: "100%",
  alignSelf: "center",
  gap: "$200",
  $lg: {
    maxWidth: 728,
  },
});

export const GridItem = styled(YStack, {
  name: "GridItem",
  flexShrink: 0,
  variants: {
    // Generates variants for colspan from 1 to 12 (DESKTOP_COLS)
    colSpan: Array.from({ length: DESKTOP_COLS }).reduce(
      (acc: Record<number, ReturnType<typeof getGridLayout>>, _, index) => {
        acc[index + 1] = getGridLayout(index + 1);
        return acc;
      },
      {},
    ),
  },
  defaultVariants: {
    colSpan: 12,
  },
});

/**
 * A wrapper view that is constrained to a maximum of the constraint property, while always maintaining
 * a set amount of horizontal padding.
 */
export const ConstrainedView = styled(YStack, {
  name: "ConstrainedView",
  maxWidth: SIZE_CONSTANTS.constraint + 2 * SIZE_CONSTANTS.screenPadding,
  width: "100%",
  alignSelf: "center",
  paddingHorizontal: "$screenPadding",
});

export const Separator = styled(TamaguiSeparator, {
  borderColor: "$strokeSubdued",
});
